/**
 * Когда выходить на смену: карта часов по СВОЕМУ ситу.
 *
 * Вилочник работает руками и не может сидеть у экрана круглосуточно. Сейчас он
 * выбирает часы ощущениями, а ощущения врут в одну сторону: помнится вечер, в
 * который взял три вилки подряд, и не помнится десяток пустых.
 *
 * Считает карту сервер по своей истории (`backend/forq/ingest/hours.py`) —
 * полтора миллиона появлений чужой ленты. Здесь только запрос и то, что нужно
 * сказать словами.
 *
 * Два свойства, ради которых этот модуль вообще отдельный:
 *
 * 1. **Карта следует за фильтрами.** В чужих конторах и чужих дисциплинах часы
 *    другие, и общая карта отвечала бы на вопрос, которого человек не задавал.
 *    Поэтому запрос уходит с текущим ситом и переспрашивается при его смене — с
 *    задержкой, потому что фишки нажимают очередями.
 * 2. **Наш простой — не спокойный рынок.** Сервер отдаёт по каждому часу число
 *    суток, в которые этот час вообще наблюдался. Час, не наблюдавшийся ни разу,
 *    рисуется прочерком, а не нулём: ноль читается как «вилок не бывает», и
 *    человек по такой карте перестал бы работать в час нашего выката.
 */

import { create } from 'zustand';

import { apiUrl } from '../lib/build';
import type { Filters } from './filters';

/** Сколько ждём тишины на фишках, прежде чем спрашивать карту заново. */
const QUIET_MS = 700;

export interface HourCell {
  hour: number;
  forks: number;
  /** Вилок за сутки наблюдений. `null` — этот час мы не видели ни разу. */
  perDay: number | null;
  /** Скольких суток касается число. */
  days: number;
}

export interface ShiftMap {
  ready: boolean;
  hours: HourCell[];
  daysObserved: number;
  episodes: number;
}

export const EMPTY_MAP: ShiftMap = { ready: false, hours: [], daysObserved: 0, episodes: 0 };

/** Запрос карты под сито. Порядок полей — как в `/v1/hours`. */
export function ask(f: Filters): string {
  const query = new URLSearchParams();
  if (f.venues.length > 0) {
    query.set('venues', f.venues.join(','));
    query.set('venueMode', f.venueMode);
  }
  if (f.mainVenues.length > 0) query.set('mainVenues', f.mainVenues.join(','));
  if (f.sports.length > 0) query.set('sports', f.sports.join(','));
  if (f.lines.length > 0) query.set('lines', f.lines.join(','));
  if (f.roiMin !== null) query.set('roiMin', String(f.roiMin));
  if (f.roiMax !== null) query.set('roiMax', String(f.roiMax));
  const tail = query.toString();
  return apiUrl(`/v1/hours${tail === '' ? '' : `?${tail}`}`);
}

/**
 * Лучшее окно суток: подряд идущие часы, где темп выше среднего.
 *
 * Подряд, а не «три лучших часа»: смену планируют отрезком, и три разбросанных
 * пика — это не расписание, а список. Ненаблюдавшиеся часы разрывают отрезок
 * молчанием, потому что про них мы не знаем ничего, а не «там мало».
 *
 * Возвращает `null`, когда карта ровная. Ровная карта — это законный ответ:
 * «работать можно когда угодно» полезнее выдуманного пика.
 */
export function bestWindow(hours: readonly HourCell[]): { from: number; to: number } | null {
  const rated = hours.filter((one) => one.perDay !== null);
  if (rated.length < 6) return null;
  const rates = rated.map((one) => one.perDay ?? 0);
  const mean = rates.reduce((sum, one) => sum + one, 0) / rates.length;
  const peak = Math.max(...rates);
  // Разброс меньше половины — карта ровная. Порог грубый намеренно: тонкая
  // граница здесь означала бы «пик» там, где его нет, а это дороже молчания.
  if (peak < mean * 1.5) return null;

  const at = (hour: number): HourCell | undefined => hours.find((one) => one.hour === hour % 24);
  const above = (hour: number): boolean => {
    const cell = at(hour);
    return cell !== undefined && cell.perDay !== null && cell.perDay >= mean;
  };

  let best: { from: number; to: number; sum: number } | null = null;
  // Сутки замкнуты: окно 22–02 существует, и разрезать его полуночью нельзя.
  // Поэтому ищем начала отрезков (час выше среднего, предыдущий — нет) и идём
  // по кругу.
  for (let start = 0; start < 24; start += 1) {
    if (!above(start) || above((start + 23) % 24)) continue;
    let sum = 0;
    let length = 0;
    while (length < 24 && above(start + length)) {
      sum += at(start + length)?.perDay ?? 0;
      length += 1;
    }
    if (best === null || sum > best.sum) {
      best = { from: start, to: (start + length - 1) % 24, sum };
    }
  }
  return best === null ? null : { from: best.from, to: best.to };
}

interface ShiftState extends ShiftMap {
  /** Идёт ли запрос. Нужен только затем, чтобы не мигать пустой картой. */
  busy: boolean;
  load: (f: Filters) => void;
}

export const useShift = create<ShiftState>((set) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let last = '';
  const fetchMap = (url: string): void => {
    set({ busy: true });
    void fetch(url, { credentials: 'same-origin' })
      .then(async (res) => (res.ok ? ((await res.json()) as Partial<ShiftMap>) : null))
      .then((body) => {
        if (body === null) {
          set({ busy: false });
          return;
        }
        set({
          ready: body.ready === true,
          hours: Array.isArray(body.hours) ? body.hours : [],
          daysObserved: typeof body.daysObserved === 'number' ? body.daysObserved : 0,
          episodes: typeof body.episodes === 'number' ? body.episodes : 0,
          busy: false,
        });
      })
      .catch(() => {
        // Карта — планирование, а не лента. Молчим и оставляем прежнюю.
        set({ busy: false });
      });
  };
  return {
    ...EMPTY_MAP,
    busy: false,
    load: (f) => {
      const url = ask(f);
      if (url === last) return;
      last = url;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => fetchMap(url), QUIET_MS);
    },
  };
});
